import { BaseApiService } from '@/shared/api/services/base.service';
import type {
  FeedbackRequest,
  SupportTicketRequest,
  SupportTicketResponse,
  // SupportTicketAttributes,
} from '../types';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';

class SupportService extends BaseApiService {
  constructor() {
    super();
  }

  /**
   * Submit a support ticket/feedback
   */
  async submitFeedback(
    feedback: FeedbackRequest
  ): Promise<SupportTicketResponse> {
    // Get current user from the Query cache (canonical owner of User per STATE_POLICY)
    const user = queryClient.getQueryData<User>(cacheKeys.user);

    if (!user) {
      throw new Error('User must be authenticated to submit feedback');
    }

    // Get browser and page info for metadata
    const metadata = {
      browser: navigator.userAgent,
      page_url: window.location.href,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };

    // Add metadata to the message
    const enhancedMessage = `${feedback.message}\n\n---\nMetadata:\n${JSON.stringify(metadata, null, 2)}`;

    // Prepare the request in JSON:API format
    const requestData: SupportTicketRequest = {
      data: {
        type: 'support-ticket',
        attributes: {
          user_name: user.email.split('@')[0] ?? 'User', // Use email prefix as name
          user_email: user.email,
          // Subject is optional: the API derives it from the message server-side.
          // We only forward a subject when an entry point seeded one (e.g. the
          // Pro Plan / pro inquiry link), so the ticket stays recognizable.
          ...(feedback.subject && feedback.subject.trim().length > 0
            ? { subject: feedback.subject }
            : {}),
          message: enhancedMessage,
          category: feedback.category,
          channel: 'web',
          ...(feedback.attachment_ids && feedback.attachment_ids.length > 0
            ? { attachment_ids: feedback.attachment_ids }
            : {}),
        },
      },
    };

    const response = await this.post<
      SupportTicketResponse,
      SupportTicketRequest
    >('/v1/support-tickets', requestData, undefined, {
      loading: { trackEndpoint: 'support.submit' },
    });

    return response;
  }

  /**
   * Get user's previous tickets (if endpoint becomes available)
   */
  async getUserTickets(): Promise<SupportTicketResponse[]> {
    // This endpoint doesn't exist yet in the API
    // Placeholder for future implementation
    throw new Error('Getting user tickets is not yet implemented');
  }
}

export const supportService = new SupportService();
